import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface LoginConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: () => void;
  onContinueAsGuest: () => void;
}

// Helper functions for guest choice storage
const GUEST_CHOICE_KEY = 'guestChoiceTimestamp';
const SIX_HOURS_IN_MS = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

const setGuestChoice = () => {
  const timestamp = Date.now();
  localStorage.setItem(GUEST_CHOICE_KEY, timestamp.toString());
};

const hasValidGuestChoice = (): boolean => {
  const stored = localStorage.getItem(GUEST_CHOICE_KEY);
  if (!stored) return false;

  const timestamp = parseInt(stored, 10);
  const now = Date.now();
  const timeDiff = now - timestamp;

  // If more than 6 hours have passed, remove the stored choice
  if (timeDiff > SIX_HOURS_IN_MS) {
    localStorage.removeItem(GUEST_CHOICE_KEY);
    return false;
  }

  return true;
};

export default function LoginConfirmationDialog({
  isOpen,
  onClose,
  onLogin,
  onContinueAsGuest,
}: LoginConfirmationDialogProps) {
  const { t } = useTranslation();

  // Check if user has already chosen to continue as guest within 6 hours
  useEffect(() => {
    if (isOpen && hasValidGuestChoice()) {
      // If user has valid guest choice, automatically continue as guest
      onContinueAsGuest();
    }
  }, [isOpen, onContinueAsGuest]);

  if (!isOpen) return null;

  const dialogContent = (
    <div className="fixed inset-0 z-[9999] transition-all duration-300 ease-out will-change-transform transform-gpu">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform animate-in zoom-in-95 duration-300">
          {/* Dialog Header */}
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{t('login_required')}</h3>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Dialog Content */}
          <div className="px-6 py-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">{t('login_required_message')}</p>
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 text-xs font-medium flex items-center gap-2">
                  {t('guest_choice_note')}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={onLogin}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
                {t('login')}
              </button>

              <button
                onClick={() => {
                  setGuestChoice();
                  onContinueAsGuest();
                }}
                className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
                {t('continue_as_guest')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(dialogContent, document.body);
}
